// Lib
import React from 'react';
import PropTypes from 'prop-types';
import { throttle, debounce } from 'lodash';

// Prop comparisons
import { noLonger, now } from '../../utils/react/propsComparisons';
import { getHasImageToShow } from './imageElementUtils';
import platformSingleton from '../../platform/platformSingleton';

const isNowHovered = now('isHovered');
const isNoLongerHovered = noLonger('isHovered');

const HOVER_DELAY_DEFAULT = 500;

const getEventPosition = (e) => {
    const touch = e.touches?.item?.(0);
    if (touch) return { x: touch.clientX, y: touch.clientY };
    return { x: e.clientX, y: e.clientY };
};

export const imageHoverWatcher = (DecoratedComponent) => {
    class ImageHoverWatcher extends React.Component {
        componentDidUpdate(prevProps) {
            if (isNoLongerHovered(prevProps, this.props)) {
                this.disableDropTarget();
            }

            if (isNowHovered(prevProps, this.props)) {
                this.onHoverStart(this.props);
            }
        }

        onHoverStart(nextProps) {
            if (!nextProps.canStartReplaceMode) return;

            // If it's a placeholder image, enable replacement mode immediately
            if (!getHasImageToShow(nextProps)) {
                this.enableReplaceMode();
                return;
            }

            this.abortController = new AbortController();
            this.debouncedEnableDropTarget();

            document.addEventListener('dragover', this.throttledOnDragOver, {
                signal: this.abortController.signal,
            });

            if (platformSingleton.features.isTouch) {
                document.addEventListener('touchmove', this.throttledOnDragOver, {
                    signal: this.abortController.signal,
                });
            }
        }

        enableReplaceMode = () => {
            const { setParentHoveredChildAcceptsDrop, dispatchStartReplaceMode, elementId } = this.props;

            dispatchStartReplaceMode(elementId);

            // Drag and Drop - Shallow hover hack
            // This will turn off a list's drop preview if the image is going to accept the drop
            setParentHoveredChildAcceptsDrop?.(true);
        };

        componentWillUnmount() {
            this.disableDropTarget();
        }

        onDragOver = (e) => {
            const eventPosition = getEventPosition(e);
            if (eventPosition.x === this.prevX && eventPosition.y === this.prevY) return;

            this.prevX = eventPosition.x;
            this.prevY = eventPosition.y;

            this.debouncedEnableDropTarget();
        };

        throttledOnDragOver = throttle(this.onDragOver, 20);

        onHoverDelayFinished = () => {
            this.enableReplaceMode();
            this.throttledOnDragOver.cancel();
            this.abortController?.abort();
        };

        debouncedEnableDropTarget = debounce(this.onHoverDelayFinished, HOVER_DELAY_DEFAULT);

        disableDropTarget = () => {
            const { setParentHoveredChildAcceptsDrop, isReplaceModeHovered, dispatchEndReplaceMode } = this.props;

            // Drag and Drop - Shallow hover hack
            // This will turn off a list's drop preview if the image is going to accept the drop
            setParentHoveredChildAcceptsDrop?.(false);

            if (isReplaceModeHovered) {
                dispatchEndReplaceMode();
            }

            this.throttledOnDragOver.cancel();
            this.debouncedEnableDropTarget.cancel();
            this.abortController?.abort();
        };

        render() {
            return <DecoratedComponent {...this.props} />;
        }
    }

    ImageHoverWatcher.propTypes = {
        element: PropTypes.object,
        placeholder: PropTypes.object,
        elementId: PropTypes.string,
        isReplaceModeHovered: PropTypes.bool,
        dispatchStartReplaceMode: PropTypes.func,
        dispatchEndReplaceMode: PropTypes.func,

        setParentHoveredChildAcceptsDrop: PropTypes.func,
    };

    return ImageHoverWatcher;
};
